import type { TFunction } from 'i18next'
import { CITY } from '@city'
import type { SuggesterStrings } from '@metro/suggester'
import { defaultRouteCardCopy, type RouteCardCopySet } from '@metro/route-card'
import { defaultStationCardCopy, type StationCardCopySet } from '@metro/station-card'
import { StationDetailsCopy, type StationDetailsCopySet } from '@metro/station-details'
import { RouteDetailsStrings, type RouteDetailsStringSet } from '@metro/route-details'
import {
  FeedbackStrings,
  type FeedbackRouteCategories,
  type FeedbackRouteReasons,
  type FeedbackStringSet,
} from '@metro/feedback'

/**
 * Host-side i18n adapters for the in-repo `@metro/*` workspace packages.
 *
 * The packages are **domain- and locale-agnostic** (see the
 * reusable-module convention): they accept their user-facing copy as a
 * plain string-set prop and ship an English `default*` set for
 * standalone use. The metro-frontend host owns the locale layer, so it
 * reads i18next here and builds each package's string-set from the
 * shipped catalogs, then passes it down as a prop. This keeps the
 * packages free of any i18n dependency while every consumed surface is
 * fully localised.
 *
 * Each builder is a pure mapping from a bound `t` (already scoped to the
 * package's namespace) onto the package's string interface. The English
 * catalog values are byte-identical to each package's `default*` set, so
 * rendering in `en` is unchanged.
 */

/**
 * Builds the {@link SuggesterStrings} set for `@metro/suggester` from
 * the `suggester` catalog namespace. `t` must be bound to that
 * namespace (`useTranslation('suggester')`).
 */
export function buildSuggesterStrings(t: TFunction): SuggesterStrings {
  return {
    searchPlaceholder: t('suggester_search_hint'),
    close: t('suggester_close'),
    clearSearch: t('suggester_search_clear'),
    dismiss: t('suggester_dismiss'),
    locationBannerTitle: t('suggester_location_title'),
    locationBannerCta: t('suggester_location_cta'),
    nearby: t('suggester_section_nearby'),
    favorites: t('suggester_section_favorites'),
    recents: t('suggester_section_recents'),
    popular: t('suggester_section_popular'),
    offlineNotice: t('suggester_offline_notice'),
    stationClosed: t('suggester_closed_badge'),
    serviceAlert: t('suggester_service_alert'),
    emptyTitle: t('suggester_empty_title'),
    emptySubtitle: t('suggester_empty_subtitle'),
    deniedAlertTitle: t('suggester_location_denied_title'),
    deniedAlertBody: t('suggester_location_denied_body'),
    deniedCancel: t('suggester_location_denied_cancel'),
    deniedOpenSettings: t('suggester_location_denied_settings'),
    disruptionTitle: t('suggester_dd_title'),
    disruptionBack: t('suggester_dd_back'),
    disruptionType: t('suggester_dd_section_type'),
    disruptionActive: t('suggester_dd_section_active'),
    disruptionAffectedLines: t('suggester_dd_section_lines'),
    disruptionWhatsHappening: t('suggester_dd_section_message'),
    disruptionSource: t('suggester_dd_source', { source: CITY.liveDataSource }),
    disruptionErrorTitle: t('suggester_dd_error_title'),
    disruptionErrorSubtitle: t('suggester_dd_error_message'),
    disruptionRetry: t('suggester_dd_error_retry'),
  }
}

/**
 * Builds the {@link RouteDetailsStringSet} for the in-repo
 * `routeDetails` surface from the `route-details` catalog namespace.
 * `t` must be bound to that namespace (`useTranslation('route-details')`).
 *
 * Each `defaultValue` is the English constant from
 * {@link RouteDetailsStrings}, so a provider-less render (or a missing
 * key) yields byte-identical English output. The `stations` plural and
 * `towards` template use i18next interpolation (`{{count}}` / `{{name}}`),
 * with the same `RouteDetailsStrings` fallbacks.
 */
export function buildRouteDetailsStrings(t: TFunction): RouteDetailsStringSet {
  const d = RouteDetailsStrings
  return {
    tapIn: t('route_details_tap_in', { defaultValue: d.tapIn }),
    tapOut: t('route_details_tap_out', { defaultValue: d.tapOut }),
    walkToThePlatform: t('route_details_walk_to_platform', {
      defaultValue: d.walkToThePlatform,
    }),
    walkToTheExit: t('route_details_walk_to_exit', { defaultValue: d.walkToTheExit }),
    makeATransfer: t('route_details_make_transfer', { defaultValue: d.makeATransfer }),
    upsellTitle: t('route_details_upsell_title', { defaultValue: d.upsellTitle }),
    reportIssue: t('route_details_report_issue', { defaultValue: d.reportIssue }),
    forTransferPrefix: t('route_details_for_transfer_prefix', {
      defaultValue: d.forTransferPrefix,
    }),
    carFirst: t('route_details_car_first', { defaultValue: d.carFirst }),
    carNearFirst: t('route_details_car_near_first', { defaultValue: d.carNearFirst }),
    carMiddle: t('route_details_car_middle', { defaultValue: d.carMiddle }),
    carNearEnd: t('route_details_car_near_end', { defaultValue: d.carNearEnd }),
    carLast: t('route_details_car_last', { defaultValue: d.carLast }),
    carAll: t('route_details_car_all', { defaultValue: d.carAll }),
    carOr: t('route_details_car_or', { defaultValue: d.carOr }),
    lineSuffix: t('route_details_line_suffix', { defaultValue: d.lineSuffix }),
    stations: (count: number) =>
      t('route_details_stations', {
        count,
        defaultValue: d.stations(count),
      }),
    towards: (stationName: string) =>
      t('route_details_towards', {
        name: stationName,
        defaultValue: d.towards(stationName),
      }),
    walkToStation: (stationName: string, lineLabel: string | null) =>
      lineLabel === null
        ? t('route_details_walk_to_station', {
            name: stationName,
            defaultValue: d.walkToStation(stationName, null),
          })
        : t('route_details_walk_to_station_line', {
            name: stationName,
            line: lineLabel,
            defaultValue: d.walkToStation(stationName, lineLabel),
          }),
    mapOf: (stationName: string) =>
      t('route_details_map_of', {
        name: stationName,
        defaultValue: d.mapOf(stationName),
      }),
  }
}

/**
 * Builds the {@link RouteCardCopySet} for `@metro/route-card` from
 * the `route-card` catalog namespace. `t` must be bound to that
 * namespace (`useTranslation('route-card')`). en values are
 * byte-identical to {@link defaultRouteCardCopy}.
 */
export function buildRouteCardCopy(t: TFunction): RouteCardCopySet {
  const d = defaultRouteCardCopy
  return {
    emptyTitle: t('route_card_empty_title', { defaultValue: d.emptyTitle }),
    emptySubtitle: t('route_card_empty_subtitle', { defaultValue: d.emptySubtitle }),
    errorTitle: t('route_card_error_title', { defaultValue: d.errorTitle }),
    errorNetworkMessage: t('route_card_error_network_message', {
      defaultValue: d.errorNetworkMessage,
    }),
    retry: t('route_card_retry', { defaultValue: d.retry }),
    routeDetails: t('route_card_route_details', { defaultValue: d.routeDetails }),
    closeAria: t('route_card_close_button_aria', { defaultValue: d.closeAria }),
    swapAria: t('route_card_swap_aria', { defaultValue: d.swapAria }),
    myLocationAria: t('route_card_my_location_aria', { defaultValue: d.myLocationAria }),
    resultsListAria: (count: number) =>
      t('route_card_results_list_aria', { count, defaultValue: d.resultsListAria(count) }),
    buildRoute: t('route_card_build_route', { defaultValue: d.buildRoute }),
    from: t('route_card_from', { defaultValue: d.from }),
    to: t('route_card_to', { defaultValue: d.to }),
  }
}

/**
 * Builds the {@link StationCardCopySet} for `@metro/station-card`
 * from the `station-card` catalog namespace. `t` must be bound to that
 * namespace (`useTranslation('station-card')`). en values are
 * byte-identical to {@link defaultStationCardCopy}.
 */
export function buildStationCardCopy(t: TFunction): StationCardCopySet {
  const d = defaultStationCardCopy
  return {
    from: t('station_card_from', { defaultValue: d.from }),
    to: t('station_card_to', { defaultValue: d.to }),
    stationInformation: t('station_card_station_information', {
      defaultValue: d.stationInformation,
    }),
    notFound: t('station_card_not_found', { defaultValue: d.notFound }),
    retry: t('station_card_retry', { defaultValue: d.retry }),
    loadingAria: t('station_card_loading_aria', { defaultValue: d.loadingAria }),
    carouselCardAria: (index: number, total: number, name: string) =>
      t('station_card_carousel_card_aria', {
        index,
        total,
        name,
        defaultValue: d.carouselCardAria(index, total, name),
      }),
    statusClosedTitle: t('station_card_status_banner_closed_title', {
      defaultValue: d.statusClosedTitle,
    }),
    statusClosedSubtitle: t('station_card_status_banner_closed_subtitle', {
      defaultValue: d.statusClosedSubtitle,
    }),
    statusRestrictedTitle: t('station_card_status_banner_restricted_title', {
      defaultValue: d.statusRestrictedTitle,
    }),
    statusRestrictedSubtitle: t('station_card_status_banner_restricted_subtitle', {
      defaultValue: d.statusRestrictedSubtitle,
    }),
    statusAdvisoryTitle: t('station_card_status_banner_advisory_title', {
      defaultValue: d.statusAdvisoryTitle,
    }),
    statusAdvisorySubtitle: t('station_card_status_banner_advisory_subtitle', {
      defaultValue: d.statusAdvisorySubtitle,
    }),
  }
}

/**
 * Builds the {@link StationDetailsCopySet} for `@metro/station-details`
 * from the `station-details` catalog namespace. `t` must be bound to
 * that namespace (`useTranslation('station-details')`).
 *
 * en values are byte-identical to the package's {@link StationDetailsCopy},
 * so the en render is unchanged.
 */
export function buildStationDetailsCopy(t: TFunction): StationDetailsCopySet {
  const d = StationDetailsCopy
  return {
    Transfers: t('station_details_transfers', { defaultValue: d.Transfers }),
    ExitsAndMap: t('station_details_exits_and_map', { defaultValue: d.ExitsAndMap }),
    Facilities: t('station_details_facilities', { defaultValue: d.Facilities }),
    FirstAndLastTrain: t('station_details_first_and_last_train', {
      defaultValue: d.FirstAndLastTrain,
    }),
    IsFavorite: t('station_details_is_favorite', { defaultValue: d.IsFavorite }),
    IsNotFavorite: t('station_details_is_not_favorite', { defaultValue: d.IsNotFavorite }),
    ReportIssue: t('station_details_report_issue', { defaultValue: d.ReportIssue }),
    StationPageLink: t('station_details_station_page_link', { defaultValue: d.StationPageLink }),
    TimetableUpsellBannerTitle: t('station_details_timetable_upsell_title', {
      defaultValue: d.TimetableUpsellBannerTitle,
    }),
    TimetableUpsellBannerDescription: t('station_details_timetable_upsell_description', {
      defaultValue: d.TimetableUpsellBannerDescription,
    }),
    TimetableUpsellBannerButton: t('station_details_timetable_upsell_button', {
      defaultValue: d.TimetableUpsellBannerButton,
    }),
    ErrorTitle: t('station_details_error_title', { defaultValue: d.ErrorTitle }),
    ErrorSubtitle: t('station_details_error_subtitle', { defaultValue: d.ErrorSubtitle }),
    ErrorRetry: t('station_details_error_retry', { defaultValue: d.ErrorRetry }),
    OfflineBanner: t('station_details_offline_banner', { defaultValue: d.OfflineBanner }),
    ZoneLabel: t('station_details_zone_suffix', { defaultValue: d.ZoneLabel }),
    ZoneAria: (badge: string) =>
      t('station_details_zone_aria', { n: badge, defaultValue: d.ZoneAria(badge) }),
    LoadingAria: t('station_details_loading_aria', { defaultValue: d.LoadingAria }),
    TimetableWeekdays: t('station_details_timetable_weekdays', {
      defaultValue: d.TimetableWeekdays,
    }),
    TimetableSaturdays: t('station_details_timetable_saturdays', {
      defaultValue: d.TimetableSaturdays,
    }),
    TimetableSundays: t('station_details_timetable_sundays', {
      defaultValue: d.TimetableSundays,
    }),
    TimetableWeekdaysAria: t('station_details_timetable_weekdays_aria', {
      defaultValue: d.TimetableWeekdaysAria,
    }),
    TimetableSaturdaysAria: t('station_details_timetable_saturdays_aria', {
      defaultValue: d.TimetableSaturdaysAria,
    }),
    TimetableSundaysAria: t('station_details_timetable_sundays_aria', {
      defaultValue: d.TimetableSundaysAria,
    }),
    TimetableFirst: t('station_details_timetable_first', { defaultValue: d.TimetableFirst }),
    TimetableLast: t('station_details_timetable_last', { defaultValue: d.TimetableLast }),
  }
}

/**
 * Builds the {@link FeedbackStringSet} for `@metro/feedback` from the
 * `feedback` catalog namespace. `t` must be bound to that namespace
 * (`useTranslation('feedback')`).
 *
 * The catalog keys are the same names the iOS `.xcstrings` and the Android
 * `values-<locale>` string resources use, and the catalogs were generated
 * from the iOS one — so the same rider sees the same wording on all three
 * platforms.
 */
export function buildFeedbackStrings(t: TFunction): FeedbackStringSet {
  const d = FeedbackStrings
  const reasons = (prefix: string, fallback: FeedbackRouteReasons): FeedbackRouteReasons => ({
    wrongRoute: t(`${prefix}_reason_wrong_route`, { defaultValue: fallback.wrongRoute }),
    tooManyTransfers: t(`${prefix}_reason_too_many_transfers`, {
      defaultValue: fallback.tooManyTransfers,
    }),
    timingWasWrong: t(`${prefix}_reason_timing_was_wrong`, {
      defaultValue: fallback.timingWasWrong,
    }),
    wrongDirection: t(`${prefix}_reason_wrong_direction`, {
      defaultValue: fallback.wrongDirection,
    }),
    hardToUse: t(`${prefix}_reason_hard_to_use`, { defaultValue: fallback.hardToUse }),
    hardToFindStations: t(`${prefix}_reason_hard_to_find_stations`, {
      defaultValue: fallback.hardToFindStations,
    }),
    incorrectMapInfo: t(`${prefix}_reason_incorrect_map_info`, {
      defaultValue: fallback.incorrectMapInfo,
    }),
    slowOrBuggy: t(`${prefix}_reason_slow_or_buggy`, { defaultValue: fallback.slowOrBuggy }),
    confusingInterface: t(`${prefix}_reason_confusing_interface`, {
      defaultValue: fallback.confusingInterface,
    }),
  })
  const routeCategories = (
    prefix: string,
    fallback: FeedbackRouteCategories,
  ): FeedbackRouteCategories => ({
    routeAccuracy: t(`${prefix}_category_route_accuracy`, {
      defaultValue: fallback.routeAccuracy,
    }),
    mapAndNavigation: t(`${prefix}_category_map_and_navigation`, {
      defaultValue: fallback.mapAndNavigation,
    }),
    appExperience: t(`${prefix}_category_app_experience`, {
      defaultValue: fallback.appExperience,
    }),
  })

  return {
    submit: t('feedback_submit', { defaultValue: d.submit }),
    notNow: t('feedback_not_now', { defaultValue: d.notNow }),
    notePlaceholder: t('feedback_note_field_placeholder', { defaultValue: d.notePlaceholder }),
    thankYouTitle: t('feedback_thank_you_title', { defaultValue: d.thankYouTitle }),
    thankYouDescription: t('feedback_thank_you_description', {
      defaultValue: d.thankYouDescription,
    }),
    route: {
      title: t('feedback_route_title', { defaultValue: d.route.title }),
      footer: t('feedback_route_footer', { defaultValue: d.route.footer }),
      categories: routeCategories('feedback_route', d.route.categories),
      reasons: reasons('feedback_route', d.route.reasons),
    },
    routeDetails: {
      title: t('feedback_route_details_title', { defaultValue: d.routeDetails.title }),
      categories: routeCategories('feedback_route_details', d.routeDetails.categories),
      reasons: reasons('feedback_route_details', d.routeDetails.reasons),
    },
    stationDetails: {
      title: t('feedback_station_details_title', { defaultValue: d.stationDetails.title }),
      categories: {
        stationInfo: t('feedback_station_details_category_station_info', {
          defaultValue: d.stationDetails.categories.stationInfo,
        }),
        mapAndNavigation: t('feedback_station_details_category_map_and_navigation', {
          defaultValue: d.stationDetails.categories.mapAndNavigation,
        }),
        appExperience: t('feedback_station_details_category_app_experience', {
          defaultValue: d.stationDetails.categories.appExperience,
        }),
      },
      reasons: {
        incorrectInfo: t('feedback_station_details_reason_incorrect_info', {
          defaultValue: d.stationDetails.reasons.incorrectInfo,
        }),
        missingInfo: t('feedback_station_details_reason_missing_info', {
          defaultValue: d.stationDetails.reasons.missingInfo,
        }),
        wrongLocation: t('feedback_station_details_reason_wrong_location', {
          defaultValue: d.stationDetails.reasons.wrongLocation,
        }),
        hardToFind: t('feedback_station_details_reason_hard_to_find', {
          defaultValue: d.stationDetails.reasons.hardToFind,
        }),
        incorrectMapInfo: t('feedback_station_details_reason_incorrect_map_info', {
          defaultValue: d.stationDetails.reasons.incorrectMapInfo,
        }),
        slowOrBuggy: t('feedback_station_details_reason_slow_or_buggy', {
          defaultValue: d.stationDetails.reasons.slowOrBuggy,
        }),
        confusingInterface: t('feedback_station_details_reason_confusing_interface', {
          defaultValue: d.stationDetails.reasons.confusingInterface,
        }),
      },
    },
  }
}
